import {
	AddTilesResponseModel,
	AddTileTargetModel,
	CreateTileRequestModel,
	GenericCreateTileModel
} from "client/Api/Workspace/workspace";
import { IFormHandler } from "client/Forms";
import {
	AlertManager,
	useAlerts,
	useUnmountedCancellation
} from "client/Hooks";
import { InputValidationResult, InputValidationResultKind } from "client/Validation/input";
import { useCallback } from "react";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { HttpRequestError } from "client/Api/httpClient";
import { TileEditMode } from "../common";
import { AppIconModel } from "client_legacy/AppIcon/common";

export interface CreateTile {
	createHandler: (target: AddTileTargetModel) => void;
}

export type CreateTileFormData<TTileModel extends GenericCreateTileModel> = Omit<TTileModel, "installedAppId" | "image" | "shouldHideName">
	& {
		image: AppIconModel;
		shouldHideName: SetOptionalModel<boolean>;
	};

export function useCreateTile<TTileModel extends GenericCreateTileModel>(
	formHandler: IFormHandler<CreateTileFormData<TTileModel>>,
	requestHandler: (
		model: CreateTileRequestModel<TTileModel>,
		signal: AbortSignal
	) => Promise<AddTilesResponseModel>,
	isLoading: boolean,
	setIsLoading: (isLoading: boolean) => void,
	mode: TileEditMode,
	onAfterSave: (response?: AddTilesResponseModel) => void,
	prepareModelForSaving?: (formData: CreateTileFormData<TTileModel>) => CreateTileFormData<TTileModel>
): CreateTile {
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const alertManager: AlertManager = useAlerts();

	const createHandler = useCallback(
		async (target: AddTileTargetModel): Promise<void> => {
			if (mode.kind !== "create") {
				throw new Error("Cannot create a tile in the wrong mode");
			}

			if (isLoading) {
				return;
			}
			try {
				const validationResult: InputValidationResult = await formHandler.validation.validate(globalCancellation);
				if (validationResult.kind === InputValidationResultKind.Invalid) {
					return;
				}

				const formValue: CreateTileFormData<TTileModel> = prepareModelForSaving?.(formHandler.value) ?? formHandler.value;
				const {
					image,
					shouldHideName,
					...restOfFormValue
				} = formValue;

				const {
					columnSpan,
					rowSpan
				} = formValue.dimension;

				const appModel: TTileModel = {
					...restOfFormValue,
					installedAppId: mode.installedAppId,
					image: {
						id: image.id,
						type: image.type,
					},
					shouldHideName: columnSpan === 2
						&& rowSpan === 2
							? shouldHideName
							: { isSet: false }

				} as unknown as TTileModel;

				setIsLoading(true);

				const response: AddTilesResponseModel = await requestHandler(
					{ target, appModel },
					globalCancellation
				);
				setIsLoading(false);

				onAfterSave(response);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}
				if (!formHandler.validation.tryProcessServerError(e)) {
					const key: LocalizationKey = e instanceof HttpRequestError && e.statusCode === 403
						? "_YouDontHavePermissionToEditYourWorkspace"
						: "_CreateTileEror";

					alertManager.showError(localize(key));
				}
				console.error(e);
				setIsLoading(false);
			}

		},
		[
			alertManager,
			formHandler,
			globalCancellation,
			isLoading,
			mode,
			requestHandler,
			setIsLoading,
			onAfterSave,
			prepareModelForSaving
		]
	);

	return {
		createHandler
	};
};
